
import React from 'react';
import { RSVPProvider, useRSVP } from '../context/RSVPContext';
import { RSVPErrorBoundary } from './RSVPErrorBoundary';
import { useUpdateRSVP } from '../hooks/useUpdateRSVP';
import { UpdateRSVPLoading } from './update-rsvp/UpdateRSVPLoading';
import { UpdateRSVPError } from './update-rsvp/UpdateRSVPError';
import { UpdateRSVPContent } from './update-rsvp/UpdateRSVPContent';

// Main Flow Content (Used inside Provider)
const UpdateRSVPFlowContent: React.FC = () => {
  const { 
    currentState, 
    eventId,
    error,
  } = useRSVP();

  // Use the custom hook to handle all the RSVP update logic
  const { 
    eventData,
    isLoading,
    eventError,
    fetchError,
    updatesAllowed,
    availableGuests,
    respondedGuests,
    inProgressGuests
  } = useUpdateRSVP(eventId!);

  // Show loading state if any data is loading
  if (isLoading) {
    return <UpdateRSVPLoading />;
  }

  // Show error state if any error occurred
  if (error || eventError || fetchError) {
    return <UpdateRSVPError error={error || eventError || fetchError} />;
  }
  
  // If updates are not allowed, don't render the RSVP flow
  if (updatesAllowed === false) {
    return null;
  }

  return (
    <UpdateRSVPContent 
      eventData={eventData!} 
      availableGuests={availableGuests}
      respondedGuests={respondedGuests}
      inProgressGuests={inProgressGuests}
    />
  );
};

// Wrapper component with Provider for update flow
interface UpdateRSVPFlowProps {
  eventId: string;
}

export const UpdateRSVPFlow: React.FC<UpdateRSVPFlowProps> = ({ eventId }) => {
  return (
    <RSVPErrorBoundary>
      <RSVPProvider 
        eventId={eventId}
        isUpdate={true}
        initialState="date_selection" // Start at date selection for updates
      >
        <UpdateRSVPFlowContent />
      </RSVPProvider>
    </RSVPErrorBoundary>
  );
};
